import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";

const STATUSES = ["received", "in_progress", "completed", "rejected"];

const TYPE_LABEL = {
    access: "View",
    correct: "Correct",
    delete: "Delete",
    withdraw: "Withdraw",
};

export default function AdminPrivacyRequests() {
    const [requests, setRequests] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const deepLinkId = searchParams.get("id");

    const load = useCallback(
        () => api.get("/privacy/requests").then((r) => setRequests(r.data || [])).finally(() => setLoading(false)),
        []
    );

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!deepLinkId || requests.length === 0) return;
        const match = requests.find((r) => r.id === deepLinkId);
        if (match) setSelected(match);
        else {
            toast.error("Request not found.");
            setSearchParams({}, { replace: true });
        }
    }, [deepLinkId, requests, setSearchParams]);

    const closeDetail = () => {
        setSelected(null);
        if (deepLinkId) setSearchParams({}, { replace: true });
    };

    const updateStatus = async (id, status) => {
        try {
            const { data } = await api.patch(`/privacy/requests/${id}`, { status });
            toast.success("Updated");
            await load();
            if (selected?.id === id) setSelected(data);
        } catch {
            toast.error("Could not update");
        }
    };

    const saveNotes = async () => {
        if (!selected) return;
        try {
            const { data } = await api.patch(`/privacy/requests/${selected.id}`, { admin_notes: selected.admin_notes || "" });
            toast.success("Notes saved");
            setSelected(data);
            await load();
        } catch {
            toast.error("Could not save");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this request record? This is destructive.")) return;
        try {
            await api.delete(`/privacy/requests/${id}`);
            toast.success("Deleted");
            closeDetail();
            await load();
        } catch {
            toast.error("Could not delete");
        }
    };

    return (
        <div className="p-10" data-testid="admin-privacy">
            <h1 className="font-serif text-3xl text-brand-ink mb-2">Data requests (PIPEDA)</h1>
            <p className="text-sm text-brand-muted mb-8">
                Access, correction, deletion, and consent-withdrawal requests submitted via
                /privacy/request. PIPEDA requires response within 30 days.
            </p>

            {loading ? (
                <div className="text-brand-muted text-sm">Loading…</div>
            ) : requests.length === 0 ? (
                <div className="text-brand-muted text-sm" data-testid="privacy-empty">No data requests yet.</div>
            ) : (
                <div className="border border-brand-line">
                    <table className="w-full text-sm">
                        <thead className="bg-brand-sand">
                            <tr className="text-left text-xs uppercase tracking-wider text-brand-muted">
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Type</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Submitted</th>
                                <th className="px-4 py-3">Completed</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map((r) => (
                                <tr
                                    key={r.id}
                                    className="border-t border-brand-line hover:bg-brand-sand/50 cursor-pointer"
                                    onClick={() => setSelected(r)}
                                    data-testid={`privacy-row-${r.id}`}
                                >
                                    <td className="px-4 py-3 text-brand-ink">{r.email}</td>
                                    <td className="px-4 py-3">{TYPE_LABEL[r.request_type] || r.request_type}</td>
                                    <td className="px-4 py-3">
                                        <span className="inline-block px-2 py-0.5 text-xs bg-brand-paper border border-brand-line">
                                            {r.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-brand-muted">
                                        {new Date(r.created_at).toLocaleDateString("en-CA")}
                                    </td>
                                    <td className="px-4 py-3 text-brand-muted">
                                        {r.completed_at ? new Date(r.completed_at).toLocaleDateString("en-CA") : "—"}
                                    </td>
                                    <td className="px-4 py-3 text-brand-ink underline">View</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selected && (
                <div
                    className="fixed inset-0 bg-brand-ink/50 z-50 flex items-end sm:items-center justify-center p-4"
                    onClick={closeDetail}
                    data-testid="privacy-detail-modal"
                >
                    <div
                        className="bg-brand-paper border border-brand-line w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6 border-b border-brand-line flex items-center justify-between">
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold">
                                    Data request
                                </div>
                                <h2 className="font-serif text-2xl text-brand-ink mt-1">
                                    {TYPE_LABEL[selected.request_type] || selected.request_type} · {selected.email}
                                </h2>
                            </div>
                            <button onClick={closeDetail} className="text-brand-muted hover:text-brand-ink">✕</button>
                        </div>
                        <div className="p-6 space-y-4 text-sm">
                            <Row label="Email" value={selected.email} />
                            <Row label="Request type" value={selected.request_type} />
                            <Row label="Details" value={selected.details || "—"} />
                            <Row label="Submitted" value={selected.created_at} />
                            <Row label="IP" value={selected.submitted_ip || "—"} />
                            <Row label="User agent" value={selected.submitted_user_agent || "—"} />
                            <Row label="Completed at" value={selected.completed_at || "—"} />
                            <div className="pt-4 border-t border-brand-line space-y-3">
                                <label className="block">
                                    <span className="text-xs uppercase tracking-wider text-brand-muted">Status</span>
                                    <select
                                        value={selected.status}
                                        onChange={(e) => updateStatus(selected.id, e.target.value)}
                                        data-testid="privacy-status-select"
                                        className="mt-2 w-full bg-transparent border border-brand-line p-2 text-brand-ink"
                                    >
                                        {STATUSES.map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="text-xs uppercase tracking-wider text-brand-muted">Internal notes</span>
                                    <textarea
                                        value={selected.admin_notes || ""}
                                        onChange={(e) => setSelected({ ...selected, admin_notes: e.target.value })}
                                        rows={4}
                                        data-testid="privacy-notes"
                                        className="mt-2 w-full bg-transparent border border-brand-line p-2 text-brand-ink"
                                        placeholder="What you did to fulfill this request, when, and any context for the audit trail."
                                    />
                                </label>
                                <div className="flex gap-3">
                                    <button
                                        onClick={saveNotes}
                                        data-testid="privacy-save-notes"
                                        className="bg-brand-ink text-brand-paper px-5 py-2 text-sm tracking-wide hover:bg-black"
                                    >
                                        Save notes
                                    </button>
                                    <button
                                        onClick={() => remove(selected.id)}
                                        data-testid="privacy-delete-button"
                                        className="text-sm text-red-700 hover:text-red-900"
                                    >
                                        Delete record
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="grid grid-cols-3 gap-4">
            <div className="text-xs uppercase tracking-wider text-brand-muted">{label}</div>
            <div className="col-span-2 text-brand-ink whitespace-pre-wrap break-words">{value || "—"}</div>
        </div>
    );
}
